import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ShieldCheck, Smartphone, KeyRound, CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

/**
 * "Claim This Business" modal — three steps:
 *   1. intro       : show explanation + Send OTP button
 *   2. otp         : enter OTP, submit, verify
 *   3. success     : confirmation + CTA to vendor dashboard
 *
 * The OTP is sent to the vendor's listed mobile (server never reveals it to the
 * claimer; only a masked form is shown).
 */
const ClaimBusinessModal = ({ open, onOpenChange, vendor, onClaimed }) => {
  const { toast } = useToast();
  const [step, setStep] = useState('intro');
  const [otp, setOtp] = useState('');
  const [maskedMobile, setMaskedMobile] = useState('');
  const [devOtp, setDevOtp] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setStep('intro'); setOtp(''); setMaskedMobile(''); setDevOtp(null); setSubmitting(false);
  };

  const handleClose = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const sendOtp = async () => {
    setSubmitting(true);
    try {
      const r = await api.claimSendOtp(vendor.id);
      setMaskedMobile(r.masked_mobile || '');
      if (r.dev_otp) setDevOtp(r.dev_otp);
      setStep('otp');
      toast({ title: 'OTP sent', description: `Code sent to the business owner mobile (${r.masked_mobile}).` });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not send OTP.';
      toast({ title: 'Could not send OTP', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (otp.length < 4) {
      toast({ title: 'Enter the 4-digit OTP', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.claimVerifyOtp(vendor.id, otp);
      // Cache the vendor profile so the Header session sync picks it up
      try {
        localStorage.setItem('tgmart_vendor', JSON.stringify(r.vendor || {}));
        window.dispatchEvent(new Event('tgmart-session-change'));
      } catch (err) {
        console.warn('claim: failed to cache vendor profile', err);
      }
      setStep('success');
      onClaimed?.(r.vendor);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Verification failed.';
      toast({ title: 'Verification failed', description: detail, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="claim-business-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#ee5e23]" />
            Claim This Business
          </DialogTitle>
          <DialogDescription>
            {step === 'intro' && `Are you the owner of ${vendor?.name}? Verify ownership by entering the OTP sent to the business mobile on file.`}
            {step === 'otp' && `Enter the 4-digit OTP sent to ${maskedMobile}.`}
            {step === 'success' && 'Your business is now claimed and verified.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'intro' && (
          <div className="space-y-4 pt-2" data-testid="claim-step-intro">
            <ul className="text-sm text-gray-700 space-y-2 bg-blue-50 border border-blue-100 rounded-md p-3">
              <li className="flex gap-2"><Smartphone className="w-4 h-4 text-[#2c54b2] shrink-0 mt-0.5" /><span>We&apos;ll send a 4-digit OTP to the mobile registered against this business.</span></li>
              <li className="flex gap-2"><KeyRound className="w-4 h-4 text-[#2c54b2] shrink-0 mt-0.5" /><span>Enter that OTP here. We never share the mobile with anyone.</span></li>
              <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /><span>Once verified, your listing shows a <b>&quot;Verified Business&quot;</b> badge and you get vendor dashboard access.</span></li>
            </ul>
            <Button
              onClick={sendOtp}
              disabled={submitting}
              className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11"
              data-testid="claim-send-otp-btn"
            >
              {submitting ? 'Sending OTP…' : 'Send OTP to Owner'}
            </Button>
            <p className="text-xs text-center text-gray-500">
              Don&apos;t have access to the registered mobile? <a href="/p/help" className="text-[#2c54b2] hover:underline">Contact support</a>
            </p>
          </div>
        )}

        {step === 'otp' && (
          <form onSubmit={verify} className="space-y-4 pt-2" data-testid="claim-step-otp">
            <div>
              <Label className="text-xs">4-digit OTP</Label>
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••"
                className="text-center tracking-widest text-lg h-12"
                autoFocus
                data-testid="claim-otp-input"
              />
            </div>
            {devOtp && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                Dev mode (SMS gateway disabled): <b>{devOtp}</b>
              </div>
            )}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11"
              data-testid="claim-verify-btn"
            >
              {submitting ? 'Verifying…' : 'Verify & Claim Business'}
            </Button>
            <button
              type="button"
              onClick={sendOtp}
              disabled={submitting}
              className="text-xs text-[#2c54b2] hover:underline w-full disabled:text-gray-400"
              data-testid="claim-resend-btn"
            >
              Resend OTP
            </button>
          </form>
        )}

        {step === 'success' && (
          <div className="space-y-4 pt-2 text-center" data-testid="claim-step-success">
            <div className="w-16 h-16 rounded-full bg-emerald-50 border-2 border-emerald-300 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Business Claimed!</h3>
              <p className="text-sm text-gray-600 mt-1">
                <b>{vendor?.name}</b> is now verified. You&apos;ve been logged in and can manage the listing.
              </p>
            </div>
            <a href="/vendor/dashboard" className="block">
              <Button className="w-full bg-[#2c54b2] hover:bg-[#1f3f87] text-white h-11" data-testid="claim-goto-dashboard-btn">
                Open Vendor Dashboard
              </Button>
            </a>
            <button
              type="button"
              onClick={() => handleClose(false)}
              className="text-xs text-gray-500 hover:underline"
            >
              Continue browsing
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ClaimBusinessModal;
